/**
 * Minimal AppShell for public-facing Round Call gameplay. Strips dashboard
 * nav, dropdowns, sign-in pill, etc. — only shows brand + share button.
 * Used for shareable public links where the audience may not be logged in.
 */
import React from "react";
import { Link } from "react-router-dom";
import { Trophy, Share, ArrowSquareOut, XLogo, WhatsappLogo, LinkSimple } from "@phosphor-icons/react";
import { toast } from "sonner";
import { DEFAULT_MD_PATH } from "../lib/wcRounds";

export const DEFAULT_SHARE_TEXT = "Try the World Cup '26 Round Call · BUY/KEEP/SELL/AVOID the 20 hottest picks of the round. Powered by Fantasy Academy.";

function buildShareUrls(url, text) {
    const u = encodeURIComponent(url);
    const t = encodeURIComponent(text);
    return {
        x: `https://x.com/intent/post?text=${t}&url=${u}`,
        whatsapp: `https://wa.me/?text=${t}%20${u}`,
    };
}

export function ShareRow({ url, text, compact = false, testidPrefix = "share" }) {
    const finalUrl = url || (typeof window !== "undefined" ? window.location.href : "");
    const finalText = text || DEFAULT_SHARE_TEXT;
    const { x, whatsapp } = buildShareUrls(finalUrl, finalText);
    const copy = async () => {
        try {
            await navigator.clipboard.writeText(finalUrl);
            toast.success("Link copied");
        } catch {
            toast.error("Couldn't copy — copy from the address bar.");
        }
    };
    const sz = compact ? 11 : 13;
    const pad = compact ? "px-2 py-1.5" : "px-3 py-2";
    return (
        <div className="flex items-center gap-1.5" data-testid={`${testidPrefix}-row`}>
            <a
                href={x}
                target="_blank"
                rel="noopener noreferrer"
                className={`font-mono text-[10px] uppercase tracking-widest text-white border border-white/30 hover:bg-white/10 ${pad} flex items-center gap-1.5`}
                data-testid={`${testidPrefix}-x-btn`}
                title="Share on X (Twitter)"
            >
                <XLogo size={sz} weight="bold" />
                {!compact && <span>X</span>}
            </a>
            <a
                href={whatsapp}
                target="_blank"
                rel="noopener noreferrer"
                className={`font-mono text-[10px] uppercase tracking-widest text-[#25D366] border border-[#25D366]/50 hover:bg-[#25D366]/10 ${pad} flex items-center gap-1.5`}
                data-testid={`${testidPrefix}-whatsapp-btn`}
                title="Share on WhatsApp"
            >
                <WhatsappLogo size={sz} weight="fill" />
                {!compact && <span>WA</span>}
            </a>
            <button
                type="button"
                onClick={copy}
                className={`font-mono text-[10px] uppercase tracking-widest text-fa-yellow border border-fa-yellow/40 hover:bg-fa-yellow/10 ${pad} flex items-center gap-1.5`}
                data-testid={`${testidPrefix}-copy-btn`}
                title="Copy link"
            >
                <LinkSimple size={sz} weight="bold" />
                {!compact && <span>Copy</span>}
            </button>
        </div>
    );
}

export default function MinimalShell({ children, shareUrl, shareLabel }) {
    const handleShare = async () => {
        const url = shareUrl || window.location.href;
        if (navigator.share) {
            try {
                await navigator.share({
                    title: shareLabel || "World Cup '26 Round Call",
                    text: DEFAULT_SHARE_TEXT,
                    url,
                });
                return;
            } catch {
                /* user cancelled — fall through to clipboard */
            }
        }
        try {
            await navigator.clipboard.writeText(url);
            toast.success("Link copied to clipboard");
        } catch {
            toast.error("Couldn't copy — copy from the address bar.");
        }
    };

    return (
        <div className="min-h-screen bg-[#0a0a0a] text-fa-cream">
            <header className="border-b border-white/10 bg-[#0d0d0d] sticky top-0 z-20">
                <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between gap-3">
                    <Link to={DEFAULT_MD_PATH} className="flex items-center gap-2 group" data-testid="play-brand">
                        <span className="bg-fa-yellow text-black p-1 group-hover:bg-fa-yellow/90 transition">
                            <Trophy size={14} weight="fill" />
                        </span>
                        <div>
                            <div className="font-display font-black text-sm uppercase tracking-tight leading-none">Round Call</div>
                            <div className="font-mono text-[8px] uppercase tracking-[0.3em] text-white/40 leading-none mt-0.5">World Cup &apos;26 · powered by Fantasy Academy</div>
                        </div>
                    </Link>
                    <div className="flex items-center gap-2">
                        {/* Dedicated X + WhatsApp + copy on desktop, native sheet on mobile */}
                        <div className="hidden sm:flex">
                            <ShareRow url={shareUrl} text={shareLabel} compact testidPrefix="play-share" />
                        </div>
                        <button
                            type="button"
                            onClick={handleShare}
                            className="sm:hidden font-mono text-[10px] uppercase tracking-widest text-fa-yellow border border-fa-yellow/40 hover:bg-fa-yellow/10 px-3 py-1.5 flex items-center gap-1.5"
                            data-testid="play-share-btn"
                        >
                            <Share size={11} weight="fill" /> Share
                        </button>
                        <a
                            href="/"
                            className="font-mono text-[10px] uppercase tracking-widest text-white/40 hover:text-white px-3 py-1.5 hidden sm:flex items-center gap-1.5"
                            data-testid="play-fantasy-academy-link"
                        >
                            <ArrowSquareOut size={10} /> Fantasy Academy
                        </a>
                    </div>
                </div>
            </header>
            <main className="max-w-5xl mx-auto px-4 py-5">
                {children}
            </main>
            <footer className="max-w-5xl mx-auto px-4 pt-6 pb-12">
                <p className="font-mono text-[9px] uppercase tracking-widest text-white/30 text-center">
                    Powered by Fantasy Academy CZ · <Link to="/worldcup" className="hover:text-white underline-offset-2 hover:underline">Full app →</Link>
                </p>
            </footer>
        </div>
    );
}
